import { request } from '@ygtechnologies/bygr-frontend-component-library';
import { LetterSetupData } from '@/seller/components/Meetings/InternalMeetings/MeetingOverview/MeetingOverview';

import { ConfigDto, MeetingListDto, PostBookingDto } from '@/types';
import { getCoreApiUrl } from '@/utils/ajax';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';

export async function getServices(residentialUnitId: number): Promise<Service[]> {
    const retur = await request(
        `v1/BookMeeting/${residentialUnitId}/services`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur.results;
}
export async function getAllServices(projectId: number): Promise<Service[]> {
    const retur = await request(
        `v1/BookMeeting/${projectId}/all-services`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur.results;
}

export async function getProjectServices(projectId: number): Promise<Service[]> {
    const retur = await request(
        `v1/BookMeeting/${projectId}/project-services`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur.Results;
}

export async function getAvailableTimesServices(
    residentialUnitId: number,
    serviceId: number,
    from: Date,
    to: Date
): Promise<AvailabilityResult> {
    const retur = await request(
        `v1/BookMeeting/${residentialUnitId}/services/${serviceId}/availableTimes`,
        {
            method: 'GET',
            json: true,
            query: {
                from: from.toJSON(),
                to: to.toJSON(),
            },
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function getNextFreeTimesServices(
    residentialUnitId: number,
    serviceId: number,
    from: Date
): Promise<AvailabilityResult> {
    const retur = await request(
        `v1/BookMeeting/${residentialUnitId}/services/${serviceId}/nextFreeTime`,
        {
            method: 'GET',
            json: true,
            query: {
                from: from.toJSON(),
            },
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function getBookings(residentialUnitId: number): Promise<BuyerMeetingDto[]> {
    const retur = await request(
        `v1/BookMeeting/${residentialUnitId}/bookings`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function getProjectBookings(projectId: number, serviceId: number): Promise<MeetingListDto> {
    const retur = await request(
        `v1/BookMeeting/project/${projectId}/bookings?serviceId=${serviceId}`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function postBooking(residentialUnitId: number, serviceId: number, data: PostBookingDto) {
    await request(
        `v1/BookMeeting/${residentialUnitId}/services/${serviceId}/book`,
        {
            method: 'POST',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function deleteBooking(residentialUnitId: number, bookingId) {
    await request(
        `v1/BookMeeting/${residentialUnitId}/bookings/${bookingId}`,
        {
            method: 'DELETE',
            json: true,
        },
        getCoreApiUrl()
    );
}

export async function hasConfig(projectId: number): Promise<boolean> {
    const retur = await request(
        `v1/BookMeeting/${projectId}/config`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function saveConfig(projectId: number, config: ConfigDto) {
    return await request(
        `v1/BookMeeting/${projectId}/config`,
        {
            method: 'POST',
            json: true,
            body: config,
        },
        getCoreApiUrl()
    );
}

export async function saveAllServices(projectId: number, config: Service[]) {
    return await request(
        `v1/BookMeeting/${projectId}/all-services`,
        {
            method: 'POST',
            json: true,
            body: config,
        },
        getCoreApiUrl()
    );
}

export async function deleteConfig(projectId: number) {
    await request(
        `v1/BookMeeting/${projectId}/config`,
        {
            method: 'DELETE',
            json: true,
        },
        getCoreApiUrl()
    );
}

export async function getAvailableRecipicants(projectId: number, meetingTypeId: number): Promise<number[]> {
    const retur = await request(
        `v1/MeetingBooking/${projectId}/availableRecipicants/${meetingTypeId}`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function getAllInternalMeetingsTypes(projectId: number): Promise<MeetingType[]> {
    const retur = await request(
        `v1/MeetingBooking/${projectId}/meetingType`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function silentSendMeetings(projectId: number, meetingTypeId: number, data: number[]) {
    await request(
        `v1/MeetingBooking/${projectId}/meetingType/${meetingTypeId}/silentSend`,
        {
            method: 'POST',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function sendMeetings(projectId: number, meetingTypeId: number, data: number[]) {
    await request(
        `v1/MeetingBooking/${projectId}/meetingType/${meetingTypeId}/send`,
        {
            method: 'POST',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function updateMeetingType(projectId: number, meetingTypeId: number, data: MeetingType) {
    await request(
        `v1/MeetingBooking/${projectId}/meetingType/${meetingTypeId}`,
        {
            method: 'PUT',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function updateMeetingTypes(projectId: number, data: MeetingType[]) {
    await request(
        `v1/MeetingBooking/${projectId}/meetingType/updateMany`,
        {
            method: 'PUT',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function adminAcceptMeeting(data: number[]) {
    await request(
        `v1/MeetingBooking/`,
        {
            method: 'PUT',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function createMeetingType(projectId: number, data: MeetingType) {
    await request(
        `v1/MeetingBooking/${projectId}/meetingType`,
        {
            method: 'POST',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}
export async function sendReminder(data: number[]) {
    await request(
        `v1/MeetingBooking/reminder`,
        {
            method: 'POST',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function getAllInternalMeetings(projectId: number): Promise<MeetingBooking[]> {
    const retur = await request(
        `v1/MeetingBooking/${projectId}`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}
export async function getAllLetterSetupData(projectId: number): Promise<LetterSetupData[]> {
    const retur = await request(
        `v1/MeetingBooking/${projectId}/letterSetupData`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}
export async function updateLetterSetupData(projectId: number, letterSetupDataId: number, data: LetterSetupData) {
    await request(
        `v1/MeetingBooking/${projectId}/letterSetupData/${letterSetupDataId}`,
        {
            method: 'PUT',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}
export async function postMultipleInternalMeetings(data: MeetingBooking[]) {
    await request(
        `v1/MeetingBooking/create-multiple`,
        {
            method: 'POST',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function deleteMultipleInternalMeetings(data: number[]) {
    await request(
        `v1/MeetingBooking/delete-multiple`,
        {
            method: 'DELETE',
            json: true,
            body: data,
        },
        getCoreApiUrl()
    );
}

export async function postFile(data: any): Promise<string> {
    const noe = await request(
        `v1/File/`,
        {
            method: 'POST',
            json: true,
            body: { fileData: data },
        },
        getCoreApiUrl()
    ).then((response) => response.text());
    console.log(noe);
    return noe;
}

export function getFileThumbnail(fileId: string): string {
    return getCoreApiUrl() + `/v1/File/${fileId}/thumbnail`;
}

export async function getFile(fileId: string): Promise<FileData> {
    const retur = await request(
        `v1/File/${fileId}/thumbnail`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export async function getInternalBuyerMeetings(residentialUnitId: number): Promise<BuyerMeetingDto[]> {
    var data = await request(
        `v1/MeetingBooking/ResidentialUnit/${residentialUnitId}`,
        {
            method: 'Get',
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    data.forEach((meeting) => {
        if (meeting.imageUrl) {
            meeting.imageUrl = getCoreApiUrl() + meeting.imageUrl;
        }
        if (meeting.fileUrl) {
            meeting.fileUrl = getCoreApiUrl() + meeting.fileUrl;
        }
    });
    return data;
}

export async function confirmMeetingForBuyer(confirmationToken: string): Promise<void> {
    await request(
        `v1/MeetingBooking/confirm/${confirmationToken}`,
        {
            method: 'Get',
        },
        getCoreApiUrl()
    );
}

export async function exportMeetings(meetingIds: number[]): Promise<void> {
    const response = await request(
        `v1/MeetingBooking/generate/export`,
        {
            method: 'POST',
            json: true,
            body: meetingIds,
        },
        getCoreApiUrl()
    );
    const pdfBlob = await response.blob();

    // Assuming 'response' is a Blob object or you have a way to convert it to a Blob
    const url = window.URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'MeetingInvitations.xlsx'; // Set the filename here
    document.body.appendChild(a); // Append the anchor to the body
    a.click(); // Programmatically click the anchor to trigger the download
    window.URL.revokeObjectURL(url); // Clean up the URL object
    a.remove(); // Remove the anchor from the body
}

export async function printLetters(
    meetingTypeId: number,
    residentialUnitIds: number[],
    download: boolean = true
): Promise<string> {
    const response = await request(
        `v1/MeetingBooking/generate/${meetingTypeId}/print`,
        {
            method: 'POST',
            json: true,
            body: residentialUnitIds,
        },
        getCoreApiUrl()
    );
    const pdfBlob = await response.blob();

    // Assuming 'response' is a Blob object or you have a way to convert it to a Blob
    const url = window.URL.createObjectURL(pdfBlob);
    if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'MeetingInvitations.pdf'; // Set the filename here
        document.body.appendChild(a); // Append the anchor to the body
        a.click(); // Programmatically click the anchor to trigger the download
        window.URL.revokeObjectURL(url); // Clean up the URL object
        a.remove(); // Remove the anchor from the body
    }
    return url;
}

export async function generatePreview(meetingTypeId: number): Promise<FileData> {
    const response = await request(
        `v1/MeetingBooking/generate/${meetingTypeId}/generatePreview`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    )
        .then((x) => x.blob())
        .then((x) => new FileData(x, 'preview.pdf'));

    return response;
}

export interface BuyerMeetingDto {
    id: number;
    source: MeetingSource;
    title: string;
    description: string;
    from: Date;
    duration: number;
    imageUrl: string;
    fileUrl?: string;
    confirmed: boolean | null;
    confirmToken: string | null;
    daysBeforeLockConfirmation: number | undefined;
}

export enum MeetingSource {
    Internal = 'Internal',
    External = 'External',
}

export interface RequestValue {
    Url: string;
    Data: any;
    Method: 'GET' | 'POST';
}

export interface ReturnValue<T> {
    Offset: number;
    Results: T[];
    Total: number;
}

export type Service = {
    id: number;
    name: string;
    description: string;
    lengthInMinutes: number;
    imageUrl: string;
    enabled: boolean;
};

export type MeetingBooking = {
    id?: number;
    residentialUnitId?: number;
    responsibleUserId?: number;
    sentDate?: Date;
    meetingType?: MeetingType;
    meetingDate?: Date | string;
    timeSlotStart?: number;
    timeSlotEnd?: number;
    meetingConfirmations?: MeetingConfirmation[];
    reminderSentDate?: Date | string;
};

export type MeetingConfirmation = {
    id: number;
    applicationUserId: number;
    confirmationDate?: Date;
    confirmedByAdmin: boolean;
};

export type MeetingType = {
    id?: number;
    name?: string;
    needsConfirmation: boolean;
    daysBeforeReminder: number;
    daysBeforeLockConfirmation: number;
};

export type BokameraProjectServiceDTO = {
    Id: number;
    BokameraServiceId: number;
    Enabled: boolean;
};

export interface AvailabilityResult {
    CompanyId: string;
    ServiceId: number;
    TimesFreeTextSingle: string;
    TimesFreeTextMultiple: string;
    Times: Array<AvailabilityResultTime>;
}

export interface AvailabilityResultTime {
    From: string;
    To: string;
    Free: number;
    FreeSpots: number;
    ExceptionTexts: string[];
}
